import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  CircularProgress, 
  Alert, 
  Button, 
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import Toast from '../../common/Toast';
import { useToast } from '../../hooks/useToast';
import { AuditLoggers } from '../../../utils/auditLogger';
import UserRights from './UserRights';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonatePageSx,
  harmonatePageTitleSx,
  harmonateCardSx,
  harmonateDataGridSx,
  harmonatePlainIconButtonSx,
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateAbortButtonSx,
  harmonateUploadOutlinedSx,
  harmonateGridActionIconSx,
  harmonateDialogContentSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogFormContentSx,
  harmonateFormFieldsSx,
  harmonateSelectMenuProps,
  harmonateTextFieldSx,
  harmonateInputLabelProps,
} from '../../../theme/harmonateStyles';
import { useFunctionPermissions } from '../../../hooks/useFunctionPermissions';
import { FC } from '../../../constants/functionCodes';

const TenantRole = () => {
  const { canAdd, canDelete } = useFunctionPermissions(FC.ROLE);
  const { toastState, showError, showSuccess, hideToast } = useToast();
  const [tenantRoles, setTenantRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedTenantRoleId, setSelectedTenantRoleId] = useState(null);
  const [selectedRoleName, setSelectedRoleName] = useState('');
  
  // Add tenant role dialog states
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [customRoleName, setCustomRoleName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchTenantRoles = async ({ silent = false } = {}) => {
    try {
      if (!silent) {
        setLoading(true);
        setError(null);
      }
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');
      
      if (!token) throw new Error('No authentication token found');
      if (!tenantId) throw new Error('No tenant ID found');

      // First, fetch the tenant roles
      const tenantRolesResp = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/roles/tenant/tenant/${tenantId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!tenantRolesResp.ok) throw new Error(`Failed to fetch tenant roles: ${tenantRolesResp.status} ${tenantRolesResp.statusText}`);
      const tenantRolesData = await tenantRolesResp.json();

      // Then, fetch all available roles to get role names
      const rolesResp = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/roles`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!rolesResp.ok) throw new Error(`Failed to fetch roles: ${rolesResp.status} ${rolesResp.statusText}`);
      const rolesData = await rolesResp.json();

      // Map tenant roles with role names
      const mappedTenantRoles = (Array.isArray(tenantRolesData) ? tenantRolesData : []).map(tenantRole => {
        const roleInfo = (Array.isArray(rolesData) ? rolesData : []).find(role => role.id === tenantRole.roleId);
        return {
          ...tenantRole,
          roleName: tenantRole.roleName || (roleInfo ? roleInfo.roleName : 'Unknown Role')
        };
      });

      setTenantRoles(mappedTenantRoles);
    } catch (err) {
      if (!silent) {
        setError(err.message);
        setTenantRoles([]);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const fetchAvailableRoles = async () => {
    setLoadingRoles(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');
      
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/roles`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!resp.ok) throw new Error('Failed to fetch roles');
      const data = await resp.json();
      
      // Filter out roles that are already assigned to this tenant
      const assignedRoleIds = tenantRoles.map(tr => tr.roleId);
      const availableRoles = (Array.isArray(data) ? data : []).filter(
        role => !assignedRoleIds.includes(role.id)
      );
      
      setAvailableRoles(availableRoles);
    } catch (err) {
      console.error('Error fetching roles:', err);
      setAvailableRoles([]);
    } finally {
      setLoadingRoles(false);
    }
  };

  const handleOpenAddDialog = () => {
    if (!canAdd) return;
    setAddDialogOpen(true);
    setSelectedRoleId('');
    setCustomRoleName('');
    fetchAvailableRoles();
  };

  const handleCloseAddDialog = () => {
    setAddDialogOpen(false);
    setSelectedRoleId('');
    setCustomRoleName('');
  };

  const handleAddTenantRole = async (e) => {
    e.preventDefault();
    const newRoleName = customRoleName.trim();
    if (!canAdd || (!selectedRoleId && !newRoleName) || (selectedRoleId && newRoleName)) return;
    
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');
      
      if (!token) throw new Error('No authentication token found');
      if (!tenantId) throw new Error('No tenant ID found');
      
      const tenantName = localStorage.getItem('tenant_name') || 'Unknown Tenant';
      const roleName = newRoleName || availableRoles.find(r => r.id === selectedRoleId)?.roleName || 'Unknown Role';
      
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/roles/tenant`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newRoleName
          ? { tenantId, roleName: newRoleName }
          : { tenantId, roleId: selectedRoleId }),
      });
      
      if (!resp.ok) {
        let message = 'Failed to add tenant role';
        try {
          const body = await resp.json();
          if (body?.message) message = body.message;
        } catch {
          // keep the default message
        }
        throw new Error(message);
      }
      
      // Log successful role addition
      await AuditLoggers.tenantRoleAdded(roleName, tenantName)
        .catch(err => console.error('Error logging audit trail:', err));
      
      handleCloseAddDialog();
      fetchTenantRoles(); // Refresh the list
    } catch (err) {
      console.error('Error adding tenant role:', err);
      showError(err.message || 'Failed to add tenant role', 'Cannot add role');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRowClick = (params, event) => {
    if (event?.target?.closest?.('button')) return;
    setSelectedTenantRoleId(params.row.id);
    setSelectedRoleName(params.row.roleName || 'Unknown Role');
    setDrawerOpen(true);
  };

  const handleOpenDelete = (row) => {
    if (!canDelete) return;
    setRoleToDelete(row);
    setDeleteDialogOpen(true);
  };

  const handleCloseDelete = () => {
    if (deleting) return;
    setDeleteDialogOpen(false);
    setRoleToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!canDelete || !roleToDelete?.id) return;
    setDeleting(true);
    const roleName = roleToDelete.roleName || 'Unknown Role';
    const tenantName = localStorage.getItem('tenant_name') || 'Unknown Tenant';
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/roles/tenant/${roleToDelete.id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!resp.ok) {
        let message = 'Failed to delete role';
        try {
          const body = await resp.json();
          if (body?.message) message = body.message;
        } catch {
          // keep the default message
        }
        throw new Error(message);
      }

      await AuditLoggers.tenantRoleRemoved(roleName, tenantName)
        .catch(err => console.error('Error logging audit trail:', err));

      if (selectedTenantRoleId === roleToDelete.id) {
        setDrawerOpen(false);
        setSelectedTenantRoleId(null);
        setSelectedRoleName('');
      }
      setDeleteDialogOpen(false);
      setRoleToDelete(null);
      showSuccess(`Role "${roleName}" removed from this tenant`, 'Role removed');
      fetchTenantRoles({ silent: true });
    } catch (err) {
      await AuditLoggers.tenantRoleRemoved(roleName, tenantName, false, err.message)
        .catch(logErr => console.error('Error logging audit trail:', logErr));
      showError(err.message || 'Failed to delete role', 'Cannot delete role');
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    fetchTenantRoles();
  }, []);

  if (loading) {
    return (
      <Box sx={{ ...harmonatePageSx, height: '100vh', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress sx={{ color: ht.white }} />
        <Typography variant="body2" sx={{ ml: 2, color: ht.white }}>Loading roles...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ ...harmonatePageSx, height: '100vh', justifyContent: 'center', alignItems: 'center' }}>
        <Alert severity="error" sx={{ mb: 3, maxWidth: '500px' }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Failed to load roles
          </Typography>
          <Typography variant="body2">
            {error}
          </Typography>
        </Alert>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={fetchTenantRoles}
          sx={harmonateProceedButtonSx}
        >
          Retry
        </Button>
      </Box>
    );
  }

  const columns = [
    { field: 'roleName', headerName: 'Role Name', flex: 1 },
    {
      field: 'actions',
      headerName: '',
      width: 70,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Tooltip title={canDelete ? 'Delete role' : 'No delete permission'}>
          <span>
            <IconButton
              size="small"
              aria-label={`Delete ${params.row.roleName || 'role'}`}
              disabled={!canDelete}
              onMouseDown={(event) => event.stopPropagation()}
              onClick={(event) => {
                event.stopPropagation();
                handleOpenDelete(params.row);
              }}
              sx={harmonateGridActionIconSx}
            >
              <DeleteIcon fontSize="small" sx={{ color: canDelete ? ht.red : undefined }} />
            </IconButton>
          </span>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box sx={{ ...harmonatePageSx, height: '100vh' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexShrink: 0 }}>
        <Typography variant="h6" sx={harmonatePageTitleSx}>
          Roles
        </Typography>
        <Tooltip title={canAdd ? 'Add role' : 'No add permission'}>
          <span>
            <IconButton
              onClick={handleOpenAddDialog}
              disabled={!canAdd}
              sx={harmonatePlainIconButtonSx}
              aria-label="Add role"
            >
              <AddIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Paper elevation={0} sx={{
        ...harmonateCardSx,
        flex: 1,
        minHeight: 0,
        mb: 3,
        display: 'flex',
        flexDirection: 'column',
      }}>
        <DataGridPro
          rows={tenantRoles.map((tr, idx) => ({
            id: tr.id || idx + 1,
            roleName: tr.roleName || '',
            roleId: tr.roleId
          }))}
          columns={columns}
          columnResizeMode="onEnd"
          resizable
          pagination
          rowsPerPageOptions={[10, 25, 50]}
          pageSize={10}
          autoPageSize={false}
          autoHeight={false}
          sx={{
            ...harmonateDataGridSx,
            flex: 1,
            minHeight: 0,
            height: '100%',
            '& .MuiDataGrid-row': { cursor: 'pointer' },
          }}
          disableSelectionOnClick
          getRowId={(row) => row.id}
          onRowClick={handleRowClick}
          rowHeight={40}
          components={{
            NoRowsOverlay: () => (
              <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <Typography variant="body1" sx={{ mb: 2, color: ht.fadedText }}>
                  No tenant roles available
                </Typography>
                <Button
                  variant="outlined"
                  onClick={fetchTenantRoles}
                  sx={harmonateUploadOutlinedSx}
                >
                  Refresh
                </Button>
              </Box>
            )
          }}
        />
      </Paper>

      {/* Add Tenant Role Dialog */}
      <Dialog open={addDialogOpen} onClose={handleCloseAddDialog} maxWidth="xs" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>Add Tenant Role</DialogTitle>
        <form onSubmit={handleAddTenantRole}>
          <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx }}>
            <TextField
              select
              label="Master role"
              name="role"
              value={selectedRoleId}
              onChange={e => {
                setSelectedRoleId(e.target.value);
                if (e.target.value) setCustomRoleName('');
              }}
              fullWidth
              size="small"
              disabled={loadingRoles || Boolean(customRoleName.trim())}
              InputLabelProps={harmonateInputLabelProps}
              SelectProps={{ MenuProps: harmonateSelectMenuProps }}
              sx={harmonateTextFieldSx}
            >
              <MenuItem value="">Select a master role</MenuItem>
              {loadingRoles ? (
                <MenuItem disabled>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                    Loading roles...
                  </Box>
                </MenuItem>
              ) : availableRoles.length === 0 ? (
                <MenuItem disabled>No available roles</MenuItem>
              ) : (
                availableRoles.map(role => (
                  <MenuItem key={role.id} value={role.id}>
                    {role.roleName}
                  </MenuItem>
                ))
              )}
            </TextField>
            <Typography variant="body2" sx={{ textAlign: 'center', color: ht.fadedText }}>
              or
            </Typography>
            <TextField
              label="New role name"
              name="customRoleName"
              value={customRoleName}
              onChange={e => {
                setCustomRoleName(e.target.value);
                if (e.target.value.trim()) setSelectedRoleId('');
              }}
              fullWidth
              size="small"
              disabled={loadingRoles || Boolean(selectedRoleId)}
              placeholder="Saved only for this tenant"
              helperText="This role is added to this tenant only and does not go into the master list."
              InputLabelProps={harmonateInputLabelProps}
              sx={harmonateTextFieldSx}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={handleCloseAddDialog} disabled={submitting} variant="outlined" sx={harmonateCancelButtonSx}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={submitting || loadingRoles || (!selectedRoleId && !customRoleName.trim())}
              sx={harmonateProceedButtonSx}
            >
              {submitting ? 'Adding...' : 'Add Role'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={handleCloseDelete} maxWidth="xs" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>Delete role</DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          <Typography sx={{ fontSize: '0.875rem', color: ht.text }}>
            Remove &quot;{roleToDelete?.roleName || 'this role'}&quot; from this tenant?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseDelete} disabled={deleting} variant="outlined" sx={harmonateCancelButtonSx}>
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} disabled={deleting} variant="contained" sx={harmonateAbortButtonSx}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <UserRights
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        tenantRoleId={selectedTenantRoleId}
        roleName={selectedRoleName}
      />
      <Toast
        open={toastState.open}
        onClose={hideToast}
        message={toastState.message}
        severity={toastState.severity}
        title={toastState.title}
        autoHideDuration={toastState.autoHideDuration}
        position={toastState.position}
        variant={toastState.variant}
      />
    </Box>
  );
};

export default TenantRole;